import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import {
  useGetActiveCheckinDefinition,
  useGetActiveProfileDefinition,
  useGetTopic,
  useGetUserToolboxProfile,
} from "../../api/queries";
import Spinner from "../../componentLibrary/Spinner";
import { ErrorDisplay } from "../../utils/ErrorHandling";
import {
  getToolboxToolSectionGroupsForTopic,
  type ToolboxToolItem,
} from "../../utils/toolboxTools";
import ActivityModal from "../ActivityPage/ActivityModal";
import { SectionPanel } from "../../componentLibrary/SectionPanel";
import ToolboxHeader from "./components/ToolboxHeader";
import CheckInCta from "./components/CheckInCta";
import AboutMyTopicPanel from "./components/AboutMyTopicPanel";
import MyToolsPanel from "./components/MyToolsPanel";
import MyPlansPanel from "./components/MyPlansPanel";
import MyProgressPanel from "./components/MyProgressPanel";

export default function ToolboxOverview() {
  const { topicId } = useParams();

  if (!topicId) {
    return <Navigate to="/error" replace />;
  }

  const { data: topic, isError, isLoading, error } = useGetTopic(topicId);
  const { data: definition } = useGetActiveProfileDefinition(topicId);
  const { data: userProfile } = useGetUserToolboxProfile(topicId);
  const { data: checkinDefinition } = useGetActiveCheckinDefinition(topicId);

  const toolboxToolSections = useMemo(
    () => (topic ? getToolboxToolSectionGroupsForTopic(topic) : []),
    [topic],
  );

  const [activityModalPageId, setActivityModalPageId] = useState<string | null>(
    null,
  );
  const [activityModalLessonId, setActivityModalLessonId] = useState<
    string | null
  >(null);

  const openToolboxTool = (tool: ToolboxToolItem) => {
    setActivityModalPageId(tool.pageId);
    setActivityModalLessonId(tool.lessonId);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  if (isError || !topic) {
    return <ErrorDisplay error={error} messageSource="api" />;
  }

  const topicHref = `/topics/${topic.slug ?? topic._id ?? topicId}`;

  return (
    <div className="space-y-4 pt-4">
      <ToolboxHeader topic={topic} topicHref={topicHref} />

      <CheckInCta topic={topic} checkinDefinition={checkinDefinition} />

      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-4">
        <div className="flex flex-col gap-4">
          <AboutMyTopicPanel
            topic={topic}
            definition={definition}
            userProfile={userProfile}
          />
          <MyToolsPanel
            topic={topic}
            toolboxToolSections={toolboxToolSections}
            onSelectTool={openToolboxTool}
          />
        </div>

        <div className="flex flex-col gap-4">
          <SectionPanel>
            <MyPlansPanel topicId={topicId} />
          </SectionPanel>
          <MyProgressPanel topic={topic} />
        </div>
      </div>

      {activityModalPageId && topic._id && (
        <ActivityModal
          pageId={activityModalPageId}
          isModalOpen={true}
          setIsModalOpen={(isOpen) => {
            if (!isOpen) setActivityModalPageId(null);
          }}
          moduleId={topic._id}
          lessonId={activityModalLessonId ?? undefined}
        />
      )}
    </div>
  );
}
